<template>
    <div class="px-2 py-4 xl:px-10">
        <div class="max-w-8xl mx-auto w-full space-y-4">
            <!-- Page Header -->
            <div class="flex items-center justify-between">
                <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                    Subjects Management
                </h1>
                <button
                    @click="openModal"
                    class="inline-flex items-center px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                >
                    <Plus class="w-4 h-4 mr-1" />
                    Add Subject
                </button>
            </div>

            <!-- Search and Filters -->
            <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4 mb-6">
                <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
                    <!-- Search -->
                    <div>
                        <label
                            class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                        >
                            Search
                        </label>
                        <input
                            v-model="searchQuery"
                            @input="debouncedSearch"
                            type="text"
                            placeholder="Search by name or code..."
                            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    <!-- Grade Level Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.grade_level"
                            :options="gradeLevelOptions"
                            label="Grade Level"
                            placeholder="All Levels"
                            option-label="label"
                            option-value="value"
                            @update:model-value="fetchSubjects"
                        />
                    </div>

                    <!-- Major Filter -->
                    <div>
                        <SearchableSelect
                            v-model="filters.major_id"
                            :options="majorOptions"
                            label="Major"
                            placeholder="All Majors"
                            option-label="name"
                            option-value="id"
                            @update:model-value="fetchSubjects"
                        />
                    </div>

                    <!-- Status Filter -->
                    <StatusFilter
                        v-model="filters.is_active"
                        @change="fetchSubjects"
                    />

                    <!-- Page Length Selector -->
                    <PageLengthSelector
                        v-model="subjectStore.perPage"
                        @update:model-value="handlePerPageChange"
                    />
                </div>
                <div class="flex justify-between items-center mt-4">
                    <div></div>
                    <button
                        @click="resetFilters"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                        <RotateCcw class="w-4 h-4 mr-1" />
                        Reset Filters
                    </button>
                </div>
            </div>

            <!-- Loading State -->
            <div
                v-if="subjectStore.loading"
                class="flex justify-center items-center py-12"
            >
                <div
                    class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"
                ></div>
            </div>

            <!-- Error State -->
            <div
                v-if="subjectStore.error"
                class="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
            >
                <div class="flex">
                    <div class="flex-shrink-0">
                        <div class="h-5 w-5 text-red-400">⚠️</div>
                    </div>
                    <div class="ml-3">
                        <h3
                            class="text-sm font-medium text-red-800 dark:text-red-200"
                        >
                            Error
                        </h3>
                        <p class="mt-1 text-sm text-red-700 dark:text-red-300">
                            {{ subjectStore.error }}
                        </p>
                        <button
                            @click="subjectStore.clearError"
                            class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-500"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            </div>

            <!-- Data Table -->
            <div
                v-if="!subjectStore.loading && !subjectStore.error"
                class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
            >
                <div
                    class="overflow-x-auto bg-gray-800 dark:bg-gray-900 shadow-md rounded-lg"
                >
                    <table
                        class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                        style="width: 100%"
                    >
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Code
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Name
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Grade Level
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Major
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Credits
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Status
                                </th>
                                <th
                                    class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                                >
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody
                            class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                        >
                            <tr
                                v-for="subject in subjectStore.subjects"
                                :key="subject.id"
                                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                                    >
                                        {{ subject.code }}
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ subject.name }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
                                >
                                    {{ formatGradeLevel(subject.grade_level) }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
                                >
                                    {{ subject.major?.name || "-" }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400"
                                >
                                    {{ subject.credit_hours }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        v-if="subject.is_active"
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                    >
                                        Active
                                    </span>
                                    <span
                                        v-else
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                    >
                                        Inactive
                                    </span>
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                                >
                                    <button
                                        @click="editSubject(subject)"
                                        title="Edit Subject"
                                        class="inline-flex items-center justify-center p-2 text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors mr-2"
                                    >
                                        <Pencil class="w-4 h-4" />
                                    </button>
                                    <button
                                        @click="confirmDelete(subject)"
                                        title="Delete Subject"
                                        class="inline-flex items-center justify-center p-2 text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                    >
                                        <Trash2 class="w-4 h-4" />
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Empty State -->
                <div
                    v-if="subjectStore.subjects.length === 0"
                    class="text-center py-12"
                >
                    <BookOpen class="mx-auto h-12 w-12 text-gray-400" />
                    <h3
                        class="mt-2 text-sm font-medium text-gray-900 dark:text-white"
                    >
                        No subjects found
                    </h3>
                    <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Get started by adding a new subject.
                    </p>
                    <div class="mt-6">
                        <button
                            @click="openModal"
                            class="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                        >
                            <Plus class="w-4 h-4 mr-1" />
                            Add Subject
                        </button>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div
                v-if="
                    (subjectStore.meta?.total || 0) >
                    (subjectStore.meta?.per_page || 10)
                "
                class="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6"
            >
                <div class="flex-1 flex justify-between sm:hidden">
                    <button
                        @click="previousPage"
                        :disabled="subjectStore.meta?.current_page === 1"
                        class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Previous
                    </button>
                    <button
                        @click="nextPage"
                        :disabled="
                            subjectStore.meta?.current_page ===
                            subjectStore.meta?.last_page
                        "
                        class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                    >
                        Next
                    </button>
                </div>
                <div
                    class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
                >
                    <div>
                        <p class="text-sm text-gray-700 dark:text-gray-300">
                            Showing
                            <span class="font-medium">{{
                                subjectStore.meta?.from ?? 0
                            }}</span>
                            to
                            <span class="font-medium">{{
                                subjectStore.meta?.to ?? 0
                            }}</span>
                            of
                            <span class="font-medium">{{
                                subjectStore.meta?.total ?? 0
                            }}</span>
                            results
                        </p>
                    </div>
                    <div>
                        <nav
                            class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                            aria-label="Pagination"
                        >
                            <button
                                @click="previousPage"
                                :disabled="
                                    subjectStore.meta?.current_page === 1
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Previous
                            </button>
                            <button
                                v-for="page in visiblePages"
                                :key="page"
                                @click="
                                    typeof page === 'number'
                                        ? goToPage(page)
                                        : null
                                "
                                :class="[
                                    'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                                    page === subjectStore.meta?.current_page
                                        ? 'z-10 bg-blue-50 border-blue-500 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300',
                                    typeof page === 'string'
                                        ? 'cursor-not-allowed'
                                        : 'cursor-pointer',
                                ]"
                                :disabled="typeof page === 'string'"
                            >
                                {{ page }}
                            </button>
                            <button
                                @click="nextPage"
                                :disabled="
                                    subjectStore.meta?.current_page ===
                                    subjectStore.meta?.last_page
                                "
                                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300"
                            >
                                Next
                            </button>
                        </nav>
                    </div>
                </div>
            </div>

            <!-- Subject Modal -->
            <SubjectModal
                v-if="showModal"
                :show-modal="showModal"
                :subject="selectedSubject"
                :is-editing="isEditing"
                @close="closeModal"
                @save="handleSave"
                @saved="handleSaved"
            />

            <!-- Delete Confirmation Modal -->
            <DeleteConfirmationModal
                v-if="showDeleteModal"
                :show="showDeleteModal"
                :title="deleteModalTitle"
                :message="deleteModalMessage"
                @confirm="handleDelete"
                @close="closeDeleteModal"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useSubjectStore } from "@/stores/subjectStore";
import { useMajorStore } from "@/stores/majorStore";
import SearchableSelect from "@/components/SearchableSelect.vue";
import SubjectModal from "@/modules/subjects/components/SubjectModal.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import StatusFilter from "@/components/StatusFilter.vue";
import PageLengthSelector from "@/components/PageLengthSelector.vue";
import NotificationService from "@/utils/notifications";
import majorApi from "@/api/major";
import type { Subject } from "@/api/subject";
import type { Major } from "@/api/major";
import { Plus, RotateCcw, Pencil, Trash2, BookOpen } from "@lucide/vue";

const subjectStore = useSubjectStore();
const majorStore = useMajorStore();
// State
const searchQuery = ref("");
const filters = ref({
    grade_level: "",
    major_id: "",
    is_active: undefined as boolean | undefined,
});
const showModal = ref(false);
const showDeleteModal = ref(false);
const selectedSubject = ref<Subject | null>(null);
const isEditing = ref(false);
const subjectToDelete = ref<Subject | null>(null);

// Delete modal properties
const deleteModalTitle = ref("Confirm Delete");
const deleteModalMessage = ref(
    "Are you sure you want to delete this subject? This action cannot be undone.",
);

// Computed
const visiblePages = computed(() => {
    const current = subjectStore.meta?.current_page || 1;
    const last = subjectStore.meta?.last_page || 1;
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
        let i = Math.max(2, current - delta);
        i <= Math.min(last - 1, current + delta);
        i++
    ) {
        range.push(i);
    }

    if (current - delta > 2) {
        rangeWithDots.push(1, "...");
    } else {
        rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (current + delta < last - 1) {
        rangeWithDots.push("...", last);
    } else if (last > 1) {
        rangeWithDots.push(last);
    }

    return rangeWithDots;
});

const hasActiveFilters = computed(() => {
    return (
        searchQuery.value.trim() !== "" ||
        filters.value.grade_level !== "" ||
        filters.value.major_id !== "" ||
        filters.value.is_active !== undefined ||
        subjectStore.page !== 1 ||
        subjectStore.perPage !== 10
    );
});

// Options for Grade Level filter
const gradeLevelOptions = computed(() => [
    { value: "", label: "All Levels" },
    { value: "X", label: "Grade X" },
    { value: "XI", label: "Grade XI" },
    { value: "XII", label: "Grade XII" },
    { value: "ALL", label: "All Grades" },
]);

// Options for Major filter
const majorOptions = computed(() => {
    const options = [{ id: "", name: "All Majors" }];
    majorStore.activeMajors.forEach((major: any) => {
        options.push({ id: major.id, name: major.name });
    });
    return options;
});

// Methods
const handlePerPageChange = (value: number) => {
    subjectStore.perPage = value;
    subjectStore.page = 1;
    fetchSubjects();
};

const resetFilters = () => {
    searchQuery.value = "";
    filters.value = {
        grade_level: "",
        major_id: "",
        is_active: undefined,
    };
    subjectStore.page = 1;
    subjectStore.perPage = 10;
    fetchSubjects();
};

const fetchSubjects = async () => {
    const searchFilters = {
        ...filters.value,
        search: searchQuery.value,
    };
    await subjectStore.fetchSubjects(searchFilters);
};

const debouncedSearch = useDebounce(fetchSubjects, 300);

const openModal = () => {
    selectedSubject.value = null;
    isEditing.value = false;
    showModal.value = true;
};

const editSubject = (subject: Subject) => {
    selectedSubject.value = subject;
    isEditing.value = true;
    showModal.value = true;
};

const closeModal = () => {
    showModal.value = false;
    selectedSubject.value = null;
    isEditing.value = false;
};

const confirmDelete = (subject: Subject) => {
    subjectToDelete.value = subject;
    deleteModalTitle.value = "Delete Subject";
    deleteModalMessage.value = `Are you sure you want to delete ${subject.name}? This action cannot be undone.`;
    showDeleteModal.value = true;
};

const closeDeleteModal = () => {
    showDeleteModal.value = false;
    subjectToDelete.value = null;
};

const handleSave = async (subjectData: any) => {
    // This function is now handled within the modal
    // Validation errors are handled in modal and don't reach here
    // Only successful operations will trigger to 'saved' event
};

const handleSaved = () => {
    // Refresh the subjects list to show new/updated subject
    fetchSubjects();
    showNotification(
        isEditing.value
            ? "Subject updated successfully"
            : "Subject created successfully",
        "success",
    );
    closeModal();
};

const handleDelete = async () => {
    try {
        if (subjectToDelete.value) {
            await subjectStore.deleteSubject(subjectToDelete.value.id);
            showNotification("Subject deleted successfully", "success");
            closeDeleteModal();
        }
    } catch (error) {
        showNotification("Failed to delete subject", "error");
    }
};

const previousPage = () => {
    if ((subjectStore.meta?.current_page || 1) > 1) {
        subjectStore.setPage((subjectStore.meta?.current_page || 1) - 1);
        fetchSubjects();
    }
};

const nextPage = () => {
    if (
        (subjectStore.meta?.current_page || 1) <
        (subjectStore.meta?.last_page || 1)
    ) {
        subjectStore.setPage((subjectStore.meta?.current_page || 1) + 1);
        fetchSubjects();
    }
};

const goToPage = (page: number) => {
    subjectStore.setPage(page);
    fetchSubjects();
};

const formatGradeLevel = (level: string) => {
    switch (level) {
        case "X":
            return "Grade X";
        case "XI":
            return "Grade XI";
        case "XII":
            return "Grade XII";
        case "ALL":
            return "All Grades";
        default:
            return level || "-";
    }
};

const showNotification = (message: string, type: "success" | "error") => {
    if (type === "success") {
        NotificationService.success(message);
    } else {
        NotificationService.error(message);
    }
};

// Debounce utility
function useDebounce(fn: Function, delay: number) {
    let timeoutId: ReturnType<typeof setTimeout>;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

// Lifecycle
onMounted(() => {
    fetchSubjects();
    majorStore.fetchActiveMajors();
});
</script>
